import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AudioDeviceInfo,
  Call,
  CallAgent,
  CallClient,
  DeviceManager,
  Features,
  LocalVideoStream,
  RemoteParticipant,
  RemoteVideoStream,
  VideoDeviceInfo,
  VideoStreamRenderer
} from '@azure/communication-calling';
import { AzureCommunicationTokenCredential } from '@azure/communication-common';

export type AcsCallState = 'none' | 'connecting' | 'ringing' | 'connected' | 'disconnected' | 'error';

export interface AcsCallLabels {
  connecting: string;
  ringing: string;
  waitingForOthers: string;
  callEnded: string;
  micDenied: string;
  cameraDenied: string;
  microphoneLabel: string;
  speakerLabel: string;
  cameraLabel: string;
}

const DEFAULT_LABELS: AcsCallLabels = {
  connecting: 'Connecting to call...',
  ringing: 'Ringing...',
  waitingForOthers: 'Waiting for others to join...',
  callEnded: 'Call ended',
  micDenied: 'Microphone access was denied. Please allow microphone access in your browser settings and try again.',
  cameraDenied: 'Camera access denied. Allow camera in browser settings to enable video.',
  microphoneLabel: 'Microphone',
  speakerLabel: 'Speaker',
  cameraLabel: 'Camera',
};

interface RemoteParticipantView {
  participant: RemoteParticipant;
  displayName: string;
  renderer: VideoStreamRenderer;
  hasVideo: boolean;
  isMuted: boolean;
}

@Component({
  selector: 'app-acs-call',
  templateUrl: './acs-call.component.html',
  styleUrls: ['./acs-call.component.scss'],
  standalone: true,
  imports: [CommonModule],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class AcsCallComponent implements OnDestroy {
  @Input() token: string;
  @Input() acsUserId: string;
  @Input() roomId: string;
  @Input() displayName: string = 'User';
  @Input() showRecordingWarning: boolean = true;
  @Input() set labels(value: Partial<AcsCallLabels>) { this._labels = { ...DEFAULT_LABELS, ...value }; }
  get labels(): AcsCallLabels { return this._labels; }
  private _labels: AcsCallLabels = { ...DEFAULT_LABELS };

  @Output() callEnded = new EventEmitter<void>();
  @Output() callError = new EventEmitter<string>();
  @Output() recordingFailed = new EventEmitter<void>();

  @ViewChild('localVideoContainer') localVideoContainer: ElementRef<HTMLDivElement>;
  @ViewChild('remoteVideoGrid') remoteVideoGrid: ElementRef<HTMLDivElement>;

  callState: AcsCallState = 'none';
  errorMessage: string = '';
  isMuted: boolean = false;
  isCameraOn: boolean = false;
  isRecordingActive: boolean = false;

  microphones: AudioDeviceInfo[] = [];
  speakers: AudioDeviceInfo[] = [];
  cameras: VideoDeviceInfo[] = [];
  selectedMicrophoneId: string = '';
  selectedSpeakerId: string = '';
  selectedCameraId: string = '';
  showDeviceMenu: boolean = false;
  isFullscreen: boolean = false;
  micPermissionDenied: boolean = false;
  cameraPermissionDenied: boolean = false;

  remoteParticipants: RemoteParticipantView[] = [];

  callDuration: string = '00:00';
  recordingWarning: boolean = false;
  private callStartTime: Date = null;
  private callTimerInterval: any = null;
  private recordingCheckTimeout: any = null;

  private callClient: CallClient;
  private callAgent: CallAgent;
  private call: Call;
  private deviceManager: DeviceManager;
  private localVideoStream: LocalVideoStream;
  private localVideoRenderer: VideoStreamRenderer;
  private remoteRenderers: Map<string, VideoStreamRenderer> = new Map();

  constructor(private ngZone: NgZone, private cdr: ChangeDetectorRef) {}

  async joinCall(): Promise<void> {
    if (!this.token || !this.roomId) {
      this.setError('Missing token or room ID.');
      return;
    }

    this.callState = 'connecting';
    this.errorMessage = '';

    try {
      const credential = new AzureCommunicationTokenCredential(this.token);

      this.callClient = new CallClient();
      this.callAgent = await this.callClient.createCallAgent(credential, {
        displayName: this.displayName
      });

      this.deviceManager = await this.callClient.getDeviceManager();

      // Request audio permission — required for the call
      const audioPermission = await this.deviceManager.askDevicePermission({ audio: true, video: false });
      if (!audioPermission.audio) {
        this.micPermissionDenied = true;
        this.setError(this.labels.micDenied);
        return;
      }
      await this.loadAudioDevices();

      // Request video permission separately so it doesn't block the call
      this.deviceManager.askDevicePermission({ audio: false, video: true })
        .then((videoPermission) => {
          this.ngZone.run(() => {
            if (!videoPermission.video) {
              this.cameraPermissionDenied = true;
            } else {
              this.cameraPermissionDenied = false;
              this.loadCameras();
            }
          });
        })
        .catch(() => {
          this.ngZone.run(() => { this.cameraPermissionDenied = true; });
        });

      this.deviceManager.on('audioDevicesUpdated', () => {
        this.ngZone.run(() => {
          this.micPermissionDenied = false;
          this.loadAudioDevices();
        });
      });

      this.deviceManager.on('videoDevicesUpdated', () => {
        this.ngZone.run(() => {
          this.cameraPermissionDenied = false;
          this.loadCameras();
        });
      });

      this.call = this.callAgent.join(
        { roomId: this.roomId },
        { audioOptions: { muted: false } }
      );

      this.call.on('stateChanged', () => {
        this.ngZone.run(() => {
          this.onCallStateChanged();
        });
      });

      this.call.on('remoteParticipantsUpdated', (e) => {
        this.ngZone.run(() => {
          e.added.forEach(p => this.addRemoteParticipant(p));
          e.removed.forEach(p => this.removeRemoteParticipant(p));
        });
      });

      const recordingFeature = this.call.feature(Features.Recording);
      this.isRecordingActive = recordingFeature.isRecordingActive;
      recordingFeature.on('isRecordingActiveChanged', () => {
        this.ngZone.run(() => {
          this.isRecordingActive = recordingFeature.isRecordingActive;
          if (this.isRecordingActive) {
            this.recordingWarning = false;
            if (this.recordingCheckTimeout) {
              clearTimeout(this.recordingCheckTimeout);
              this.recordingCheckTimeout = null;
            }
          }
        });
      });

      // Subscribe to any participants already in the call
      this.call.remoteParticipants.forEach(p => this.addRemoteParticipant(p));
    } catch (error) {
      this.setError(error?.message || 'Failed to join call.');
    }
  }

  async toggleMute(): Promise<void> {
    if (!this.call) return;

    try {
      if (this.isMuted) {
        await this.call.unmute();
      } else {
        await this.call.mute();
      }
      this.isMuted = !this.isMuted;
    } catch (error) {
      // Silently handle - mute state may be out of sync
    }
  }

  async selectMicrophone(deviceId: string): Promise<void> {
    if (!this.deviceManager) return;

    const mic = this.microphones.find(m => m.id === deviceId);
    if (mic) {
      await this.deviceManager.selectMicrophone(mic);
      this.selectedMicrophoneId = deviceId;
    }
  }

  async selectSpeaker(deviceId: string): Promise<void> {
    if (!this.deviceManager) return;

    const speaker = this.speakers.find(s => s.id === deviceId);
    if (speaker) {
      await this.deviceManager.selectSpeaker(speaker);
      this.selectedSpeakerId = deviceId;
    }
  }

  toggleDeviceMenu(): void {
    this.showDeviceMenu = !this.showDeviceMenu;
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
  }

  async toggleCamera(): Promise<void> {
    if (!this.call || !this.deviceManager) return;

    if (this.cameraPermissionDenied) return;

    try {
      if (this.isCameraOn) {
        await this.call.stopVideo(this.localVideoStream);
        this.disposeLocalVideoRenderer();
        this.isCameraOn = false;
      } else {
        const camera = this.cameras.find(c => c.id === this.selectedCameraId) || this.cameras[0];
        if (!camera) return;

        this.localVideoStream = new LocalVideoStream(camera);
        await this.call.startVideo(this.localVideoStream);
        this.isCameraOn = true;
        this.cdr.detectChanges(); // Ensure *ngIf renders the container before we append video
        await this.renderLocalVideo();
      }
    } catch (error) {
      // Silently handle - camera may not be available
    }
  }

  async selectCamera(deviceId: string): Promise<void> {
    if (!this.deviceManager) return;

    const camera = this.cameras.find(c => c.id === deviceId);
    if (!camera) return;

    this.selectedCameraId = deviceId;

    if (this.isCameraOn && this.localVideoStream) {
      await this.localVideoStream.switchSource(camera);
    }
  }

  async hangUp(): Promise<void> {
    if (this.call) {
      try {
        await this.call.hangUp();
      } catch (error) {
        // Call may already be disconnected
      }
    }
    this.cleanup();
    this.callState = 'disconnected';
    this.callEnded.emit();
  }

  private async loadAudioDevices(): Promise<void> {
    if (!this.deviceManager) return;

    this.microphones = await this.deviceManager.getMicrophones();
    this.speakers = await this.deviceManager.getSpeakers();

    // Set initial selection to current device if available
    const currentMic = this.deviceManager.selectedMicrophone;
    if (currentMic) {
      this.selectedMicrophoneId = currentMic.id;
    } else if (this.microphones.length > 0) {
      this.selectedMicrophoneId = this.microphones[0].id;
    }

    const currentSpeaker = this.deviceManager.selectedSpeaker;
    if (currentSpeaker) {
      this.selectedSpeakerId = currentSpeaker.id;
    } else if (this.speakers.length > 0) {
      this.selectedSpeakerId = this.speakers[0].id;
    }
  }

  private async loadCameras(): Promise<void> {
    if (!this.deviceManager) return;

    this.cameras = await this.deviceManager.getCameras();

    if (this.cameras.length > 0 && !this.selectedCameraId) {
      this.selectedCameraId = this.cameras[0].id;
    }
  }

  private async renderLocalVideo(): Promise<void> {
    if (!this.localVideoStream || !this.localVideoContainer) return;

    this.disposeLocalVideoRenderer();
    this.localVideoRenderer = new VideoStreamRenderer(this.localVideoStream);
    const view = await this.localVideoRenderer.createView({ scalingMode: 'Crop', isMirrored: true });

    this.localVideoContainer.nativeElement.innerHTML = '';
    this.localVideoContainer.nativeElement.appendChild(view.target);
  }

  private disposeLocalVideoRenderer(): void {
    if (this.localVideoRenderer) {
      this.localVideoRenderer.dispose();
      this.localVideoRenderer = null;
    }
    if (this.localVideoContainer) {
      this.localVideoContainer.nativeElement.innerHTML = '';
    }
  }

  private addRemoteParticipant(participant: RemoteParticipant): void {
    const participantView: RemoteParticipantView = {
      participant,
      displayName: participant.displayName || 'Participant',
      renderer: null,
      hasVideo: false,
      isMuted: participant.isMuted
    };

    this.remoteParticipants.push(participantView);

    participant.on('isMutedChanged', () => {
      this.ngZone.run(() => {
        participantView.isMuted = participant.isMuted;
      });
    });

    participant.on('videoStreamsUpdated', (e) => {
      this.ngZone.run(() => {
        e.added.forEach(stream => this.subscribeToRemoteStream(participant, stream));
        e.removed.forEach(() => this.disposeRemoteRenderer(participant));
      });
    });

    // Subscribe to existing video streams
    participant.videoStreams.forEach(stream => {
      this.subscribeToRemoteStream(participant, stream);
    });
  }

  private removeRemoteParticipant(participant: RemoteParticipant): void {
    this.disposeRemoteRenderer(participant);
    this.remoteParticipants = this.remoteParticipants.filter(
      p => p.participant !== participant
    );
  }

  private subscribeToRemoteStream(participant: RemoteParticipant, stream: RemoteVideoStream): void {
    const pView = this.remoteParticipants.find(p => p.participant === participant);
    if (!pView) return;

    stream.on('isAvailableChanged', () => {
      this.ngZone.run(() => {
        if (stream.isAvailable) {
          this.renderRemoteStream(participant, stream);
        } else {
          this.disposeRemoteRenderer(participant);
          pView.hasVideo = false;
        }
      });
    });

    if (stream.isAvailable) {
      this.renderRemoteStream(participant, stream);
    }
  }

  private async renderRemoteStream(participant: RemoteParticipant, stream: RemoteVideoStream): Promise<void> {
    const pView = this.remoteParticipants.find(p => p.participant === participant);
    if (!pView) return;

    try {
      this.disposeRemoteRenderer(participant);
      const renderer = new VideoStreamRenderer(stream);
      const view = await renderer.createView({ scalingMode: 'Crop' });

      const key = this.getParticipantKey(participant);
      this.remoteRenderers.set(key, renderer);
      pView.renderer = renderer;
      pView.hasVideo = true;

      // Append to the participant's video container in the DOM
      setTimeout(() => {
        const container = this.remoteVideoGrid?.nativeElement?.querySelector(
          `[data-participant-id="${key}"]`
        );
        if (container) {
          container.innerHTML = '';
          container.appendChild(view.target);
        }
      });
    } catch (error) {
      // Remote stream may have been removed
    }
  }

  private disposeRemoteRenderer(participant: RemoteParticipant): void {
    const key = this.getParticipantKey(participant);
    const renderer = this.remoteRenderers.get(key);
    if (renderer) {
      renderer.dispose();
      this.remoteRenderers.delete(key);
    }
    const pView = this.remoteParticipants.find(p => p.participant === participant);
    if (pView) {
      pView.renderer = null;
      pView.hasVideo = false;
    }
  }

  private getParticipantKey(participant: RemoteParticipant): string {
    return participant.identifier?.['communicationUserId']
      || participant.identifier?.['rawId']
      || participant.displayName
      || 'unknown';
  }

  private onCallStateChanged(): void {
    if (!this.call) return;

    const state = this.call.state;

    switch (state) {
      case 'Connecting':
      case 'EarlyMedia':
        this.callState = 'connecting';
        break;
      case 'Ringing':
        this.callState = 'ringing';
        break;
      case 'Connected':
        this.callState = 'connected';
        this.startCallTimer();
        this.startRecordingCheck();
        break;
      case 'Disconnected':
        this.callState = 'disconnected';
        this.stopCallTimer();
        this.cleanup();
        this.callEnded.emit();
        break;
      case 'Disconnecting':
        // transitional, no UI change
        break;
      default:
        break;
    }
  }

  private setError(message: string): void {
    this.callState = 'error';
    this.errorMessage = message;
    this.callError.emit(message);
  }

  private startRecordingCheck(): void {
    // Give the server 15 seconds to start recording after the call connects
    this.recordingCheckTimeout = setTimeout(() => {
      this.ngZone.run(() => {
        if (!this.isRecordingActive && this.callState === 'connected') {
          this.recordingWarning = true;
          this.recordingFailed.emit();
        }
      });
    }, 15000);
  }

  private startCallTimer(): void {
    this.callStartTime = new Date();
    this.callDuration = '00:00';
    this.callTimerInterval = setInterval(() => {
      this.ngZone.run(() => {
        const elapsed = Math.floor((Date.now() - this.callStartTime.getTime()) / 1000);
        const hours = Math.floor(elapsed / 3600);
        const minutes = Math.floor((elapsed % 3600) / 60);
        const seconds = elapsed % 60;
        const pad = (n: number) => n.toString().padStart(2, '0');
        this.callDuration = hours > 0
          ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
          : `${pad(minutes)}:${pad(seconds)}`;
      });
    }, 1000);
  }

  private stopCallTimer(): void {
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }
  }

  private cleanup(): void {
    this.stopCallTimer();
    if (this.recordingCheckTimeout) {
      clearTimeout(this.recordingCheckTimeout);
      this.recordingCheckTimeout = null;
    }
    this.recordingWarning = false;
    this.isRecordingActive = false;
    this.disposeLocalVideoRenderer();
    this.remoteRenderers.forEach(r => r.dispose());
    this.remoteRenderers.clear();

    if (this.callAgent) {
      this.callAgent.dispose();
      this.callAgent = null;
    }
    this.call = null;
    this.deviceManager = null;
    this.localVideoStream = null;
    this.isMuted = false;
    this.isCameraOn = false;
    this.isFullscreen = false;
    this.showDeviceMenu = false;
    this.microphones = [];
    this.speakers = [];
    this.cameras = [];
    this.remoteParticipants = [];
    this.callDuration = '00:00';
    this.micPermissionDenied = false;
    this.cameraPermissionDenied = false;
  }

  ngOnDestroy(): void {
    if (this.call && this.call.state === 'Connected') {
      this.call.hangUp().catch(() => {});
    }
    this.cleanup();
  }
}
